import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { extractErrorMessage } from "@/shared/utils";
import {
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useGetProjectMembersQuery,
  useAddProjectMemberMutation,
  useRemoveProjectMemberMutation,
  useGetOrgMembersQuery,
} from "../api";
import { createProjectFormSchema, type ProjectFormValues } from "../validation";
import { PLANNED_FIELDS, stripTrailingZeros } from "../utils/plannedFields";
import type { PlannedField } from "../utils/plannedFields";
import {
  isInsufficientExternalPoolError,
  isSourcesSmetaMismatchError,
  isExternalSmetaReadonlyError,
  isInternalImmutableError,
} from "../utils/errorGuards";
import type { Project, ProjectFormPayload } from "../types";

interface UseProjectFormProps {
  project: Project | null;
  onSuccess: () => void;
  mode: "create" | "edit";
}

const EMPTY_PLANNED = Object.fromEntries(PLANNED_FIELDS.map((f) => [f, ""])) as Record<PlannedField, string>;

export function useProjectForm({ project, onSuccess, mode }: UseProjectFormProps) {
  const { t } = useTranslation("ProjectsPage");
  const [errorPrompt, setErrorPrompt] = useState<{ title: string; text: string } | null>(null);
  const [existingPictureUrl, setExistingPictureUrl] = useState<string | null>(null);

  const schema = createProjectFormSchema(mode);

  const form = useForm<ProjectFormValues>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
      status: "planned",
      is_internal: false,
      sources: [],
      ...EMPTY_PLANNED,
      start_date: "",
      end_date: "",
      region_id: null,
      category: "",
      project_picture: null,
      beneficiaries_coverage: "",
      partners: [],
    },
  });

  const { reset } = form;

  const [createProject, { isLoading: isCreating }] = useCreateProjectMutation();
  const [updateProject, { isLoading: isUpdating }] = useUpdateProjectMutation();

  const projectId = project?.id ?? null;
  const { data: members, isLoading: isMembersLoading, isFetching: isMembersFetching } = useGetProjectMembersQuery(projectId!, {
    skip: mode !== "edit" || projectId === null,
  });
  const { data: orgMembers, isLoading: isOrgMembersLoading } = useGetOrgMembersQuery(undefined, {
    skip: mode !== "edit",
  });

  const [addMember] = useAddProjectMemberMutation();
  const [removeMember] = useRemoveProjectMemberMutation();
  const [addingMember, setAddingMember] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState<number | null>(null);
  const [confirmRemoveMemberId, setConfirmRemoveMemberId] = useState<number | null>(null);
  const [pendingRemoveId, setPendingRemoveId] = useState<number | null>(null);
  const [pendingAdd, setPendingAdd] = useState(false);

  useEffect(() => {
    if (mode === "edit" && project) {
      setExistingPictureUrl(project.project_picture_url || null);
      const plannedValues = PLANNED_FIELDS.reduce<Partial<Record<PlannedField, string>>>((acc, field) => {
        acc[field] = stripTrailingZeros(project[field]);
        return acc;
      }, {});
      reset({
        name: project.name || "",
        description: project.description || "",
        status: project.status === "archived" ? "closed" : project.status,
        is_internal: project.is_internal,
        sources: [],
        ...EMPTY_PLANNED,
        ...plannedValues,
        start_date: project.start_date || "",
        end_date: project.end_date || "",
        region_id: project.region?.id ?? null,
        category: project.category || "",
        project_picture: null,
        beneficiaries_coverage: project.beneficiaries_coverage !== null ? String(project.beneficiaries_coverage) : "",
        partners: project.partners || [],
      });
    } else if (mode === "create") {
      setExistingPictureUrl(null);
      reset({
        name: "",
        description: "",
        status: "planned",
        is_internal: false,
        sources: [],
        ...EMPTY_PLANNED,
        start_date: "",
        end_date: "",
        region_id: null,
        category: "",
        project_picture: null,
        beneficiaries_coverage: "",
        partners: [],
      });
    }
  }, [project, reset, mode]);

  useEffect(() => {
    if (!isMembersFetching && pendingRemoveId !== null) {
      setPendingRemoveId(null);
      setRemovingMemberId(null);
    }
  }, [isMembersFetching, pendingRemoveId]);

  useEffect(() => {
    if (!isMembersFetching && pendingAdd) {
      setPendingAdd(false);
      setAddingMember(false);
    }
  }, [isMembersFetching, pendingAdd]);

  function handleRemoveExistingPicture() {
    setExistingPictureUrl(null);
  }

  async function handleSubmit(values: ProjectFormValues) {
    setErrorPrompt(null);
    try {
      const plannedFields = PLANNED_FIELDS.reduce<Partial<Record<PlannedField, string>>>((acc, field) => {
        const value = values[field];
        if (value !== undefined && value !== "") acc[field] = value;
        return acc;
      }, {});

      const beneficiariesNum = values.beneficiaries_coverage
        ? parseInt(values.beneficiaries_coverage, 10)
        : null;

      const isInternal = values.is_internal === true;

      const payload: ProjectFormPayload = {
        name: values.name || undefined,
        description: values.description || undefined,
        status: values.status,
        is_internal: mode === "create" ? isInternal : undefined,
        income_sources: mode === "create" && !isInternal ? values.sources : undefined,
        ...(mode === "create" ? plannedFields : {}),
        start_date: values.start_date || undefined,
        end_date: values.end_date || undefined,
        region_id: values.region_id ?? null,
        category: values.category || undefined,
        project_picture: values.project_picture || null,
        beneficiaries_coverage: isNaN(beneficiariesNum as number) ? null : beneficiariesNum,
        partners: values.partners,
      };

      if (mode === "create") {
        await createProject({
          ...payload,
          name: values.name!,
        }).unwrap();
        reset();
      } else if (project) {
        await updateProject({
          id: project.id,
          data: payload,
        }).unwrap();
      }
      onSuccess();
    } catch (error) {
      const titleKey = mode === "create" ? "prompt.projectCreateErrorTitle" : "prompt.projectUpdateErrorTitle";
      const errorKey = mode === "create" ? "toast.projectCreateError" : "toast.projectUpdateError";
      const text = isInsufficientExternalPoolError(error)
        ? t("projectForm.errors.insufficientExternalPool", {
            available: error.data.available,
          })
        : isSourcesSmetaMismatchError(error)
          ? t("projectForm.errors.sourcesSmetaMismatch")
          : isExternalSmetaReadonlyError(error)
            ? t("projectForm.errors.externalSmetaReadonly")
            : isInternalImmutableError(error)
              ? t("projectForm.errors.internalImmutable")
              : extractErrorMessage(error, errorKey, t);
      setErrorPrompt({
        title: t(titleKey),
        text,
      });
    }
  }

  async function handleAddMember(userId: number) {
    if (projectId === null || addingMember) return;
    try {
      setAddingMember(true);
      await addMember({ projectId, userId }).unwrap();
      setPendingAdd(true);
    } catch {
      setAddingMember(false);
    }
  }

  function requestRemoveMember(userId: number) {
    setConfirmRemoveMemberId(userId);
  }

  function cancelRemoveMember() {
    setConfirmRemoveMemberId(null);
  }

  async function confirmRemoveMember() {
    if (projectId === null || confirmRemoveMemberId === null || removingMemberId !== null) return;
    try {
      setRemovingMemberId(confirmRemoveMemberId);
      setConfirmRemoveMemberId(null);
      await removeMember({ projectId, userId: confirmRemoveMemberId }).unwrap();
      setPendingRemoveId(confirmRemoveMemberId);
    } catch (error) {
      setRemovingMemberId(null);
      setConfirmRemoveMemberId(null);
      setErrorPrompt({
        title: t("members.removeErrorTitle"),
        text: extractErrorMessage(error, "members.removeError", t),
      });
    }
  }

  function handleCloseErrorPrompt() {
    setErrorPrompt(null);
  }

  const memberIds = new Set(members?.map((m) => m.id) ?? []);
  const availableOrgMembers = (orgMembers ?? []).filter((m) => !memberIds.has(m.id));

  return {
    register: form.register,
    watch: form.watch,
    setValue: form.setValue,
    errors: form.formState.errors,
    isValid: form.formState.isValid,
    isSubmitting: isCreating || isUpdating,
    errorPrompt,
    handleCloseErrorPrompt,
    handleSubmit: form.handleSubmit(handleSubmit),
    members: members ?? [],
    isMembersLoading,
    isMembersFetching,
    availableOrgMembers,
    isOrgMembersLoading,
    addingMember,
    removingMemberId,
    confirmRemoveMemberId,
    handleAddMember,
    requestRemoveMember,
    cancelRemoveMember,
    confirmRemoveMember,
    existingPictureUrl,
    handleRemoveExistingPicture,
  };
}
